import React from 'react';

import {
  ActionIcon,
  Box,
  Button,
  Card,
  createStyles,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconDeviceDesktop, IconLock, IconPlus, IconX } from '@tabler/icons-react';
import ExpandableSection from 'components/ExpandableSection';
import WorkoutRichTextEditor from 'components/workout-program/WorkoutRichTextEditor';
import { useAuth } from 'context/auth-context';
import { useScrollContainer } from 'hooks/useScrollContainer';
import useUpdateWorkoutTemplate from 'hooks/workoutTemplate/useUpdateWorkoutTemplate';
import useWorkoutTemplate from 'hooks/workoutTemplate/useWorkoutTemplate';
import { useNavigate, useParams } from 'react-router-dom';
import { WorkoutTemplateRetrieve, WorkoutTemplateWrite } from 'types';
import { DistanceUnitEnum, ScoreTypeEnum, WeightUnitEnum } from 'types/enums';
import { getAlphabetLetterByIndex } from 'utils/functions';
import notificationHandler from 'utils/notifications';

import { createEmptySection, duplicateSection } from '../../../forms/workoutBuilder/createSection';
import { inferSectionMode, isKeptBenchmarkSection } from '../../../forms/workoutBuilder/inferSectionMode';
import {
  collectSectionErrors,
  extractMovementScoringPayload,
  hasMovementScoring,
} from '../../../forms/workoutBuilder/scoringFormHelpers';
import { WorkoutSectionData, WorkoutTemplateFormValues } from '../../../forms/workoutBuilder/types';
import { notifyWorkoutValidationErrors } from '../../../forms/workoutBuilder/workoutValidationToast';
import ModalWorkoutTemplatePreview, {
  WorkoutTemplatePreviewData,
} from '../../components/WorkoutLibrary/WorkoutTemplates/components/ModalWorkoutTemplatePreview';
import WorkoutSection from '../CreateWorkoutTemplate/components/WorkoutSection';

const useStyles = createStyles(theme => ({
  page: {
    height: '100%',
    overflowY: 'auto',
    backgroundColor: theme.colors.gray[0],
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '1.5rem',
    paddingBottom: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    borderBottom: `0.0625rem solid ${theme.colors.gray[3]}`,
    backgroundColor: theme.white,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  headerLeft: {
    flex: 1,
  },
  headerActions: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    flexShrink: 0,
  },
  previewLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    '&:hover': {
      color: theme.colors.gray[9],
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  card: {
    marginBottom: '1rem',
    backgroundColor: theme.white,
    overflow: 'visible',
  },
  addSectionBar: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: theme.colors.gray[0],
    paddingTop: '1rem',
    paddingBottom: '1.5rem',
    zIndex: 10,
  },
  addSectionButton: {
    width: '100%',
  },
}));

function mapRetrieveToFormValues(template: WorkoutTemplateRetrieve): WorkoutTemplateFormValues {
  return {
    name: template.name,
    athleteNotes: template.athleteNotes ?? '',
    coachNotes: template.coachNotes ?? '',
    sections: template.sections.map(
      (sec, i): WorkoutSectionData => ({
        id: sec.workoutSectionTemplateId,
        letter: getAlphabetLetterByIndex(i),
        name: sec.name,
        description: sec.workoutInstructions ?? '',
        scoreEntireSection: inferSectionMode(sec) === 'section_level',
        saveAsBenchmark: inferSectionMode(sec) === 'section_level' ? isKeptBenchmarkSection(sec) : true,
        levelScaled: sec.levelScaled,
        levelRx: sec.levelRx,
        levelRxPlus: sec.levelRxPlus,
        movements: sec.movements.map(m => ({
          id: m.workoutSectionMovementTemplateId,
          movementId: m.workoutMovement?.workoutMovementId ?? m.workoutMovementId ?? '',
          movementName: m.workoutMovement?.name ?? '',
          scoreType: m.scoreType as ScoreTypeEnum,
          distanceUnit: (m.distanceUnit as DistanceUnitEnum | '' | null) || null,
          weightUnit: (m.weightUnit as WeightUnitEnum | '' | null) || null,
          scoreSets: (m.scoreSets ?? []).map(s => ({
            setIndex: s.setIndex,
            targetReps: s.targetReps,
          })),
        })),
      }),
    ),
  };
}

interface EditWorkoutTemplateFormProps {
  template: WorkoutTemplateRetrieve;
  workoutTemplateId: string;
}

const EditWorkoutTemplateForm = ({
  template,
  workoutTemplateId,
}: EditWorkoutTemplateFormProps): JSX.Element => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [athleteNotesOpened, { toggle: toggleAthleteNotes }] = useDisclosure(false);
  const [coachNotesOpened, { toggle: toggleCoachNotes }] = useDisclosure(false);
  const [previewModalOpened, { open: openPreviewModal, close: closePreviewModal }] = useDisclosure(false);
  const { containerRef, scrollToBottom } = useScrollContainer<HTMLDivElement>();
  const updateMutation = useUpdateWorkoutTemplate();
  const { successNotification, errorNotification } = notificationHandler();
  const { hasPerm } = useAuth();
  const canMutate = hasPerm('WorkoutMutate');

  const form = useForm<WorkoutTemplateFormValues>({
    initialValues: mapRetrieveToFormValues(template),
    validate: {
      name: value => (value.trim().length === 0 ? 'Workout name is required' : null),
    },
  });

  const updateSectionLetters = (sections: WorkoutSectionData[]): WorkoutSectionData[] => {
    return sections.map((section, index) => ({
      ...section,
      letter: getAlphabetLetterByIndex(index),
    }));
  };

  const handleAddSection = (): void => {
    const currentSections = form.values.sections;
    const nextLetter = getAlphabetLetterByIndex(currentSections.length);
    const newSection = createEmptySection({ letter: nextLetter });
    const updatedSections = [...currentSections, newSection];
    form.setFieldValue('sections', updateSectionLetters(updatedSections));
    scrollToBottom();
  };

  const handleDuplicateSection = (index: number): void => {
    const currentSections = form.values.sections;
    const sectionToDuplicate = currentSections[index];
    const duplicatedSection = duplicateSection(sectionToDuplicate);
    const newSections = [
      ...currentSections.slice(0, index + 1),
      duplicatedSection,
      ...currentSections.slice(index + 1),
    ];

    form.setFieldValue('sections', updateSectionLetters(newSections));
    successNotification('Section duplicated', 'The section has been duplicated.');
  };

  const handleDeleteSection = (index: number): void => {
    if (form.values.sections.length > 1) {
      const currentSections = form.values.sections;
      const newSections = currentSections.filter((_, i) => i !== index);
      form.setFieldValue('sections', updateSectionLetters(newSections));
    }
  };

  const buildPayload = (values: WorkoutTemplateFormValues): WorkoutTemplateWrite => {
    return {
      workoutTemplateId: workoutTemplateId!,
      name: values.name.trim(),
      athleteNotes: values.athleteNotes?.trim() || null,
      coachNotes: values.coachNotes?.trim() || null,
      sections: values.sections.map((section, sectionIndex) => ({
        workoutSectionTemplateId: section.id,
        name: section.name.trim(),
        workoutInstructions: section.description.trim(),
        orderIndex: sectionIndex,
        levelScaled: section.levelScaled,
        levelRx: section.levelRx,
        levelRxPlus: section.levelRxPlus,
        scoreEntireSection: section.scoreEntireSection,
        movements: (section.scoreEntireSection
          ? section.movements.filter(m => m.scoreType !== null)
          : section.movements.filter(hasMovementScoring)
        ).map((m, movementIndex) => ({
          workoutSectionMovementTemplateId: m.id,
          workoutMovement: {} as never,
          workoutMovementId: section.scoreEntireSection
            ? m.movementId || (null as unknown as string)
            : m.movementId,
          saveAsBenchmark: section.saveAsBenchmark,
          orderIndex: movementIndex,
          ...extractMovementScoringPayload(m),
        })),
      })),
    };
  };

  const validateWorkout = (actionLabel: string): boolean => {
    const { errors: nameErrors } = form.validate();
    const sectionErrors = collectSectionErrors(form.values.sections, form.setFieldError);
    const allErrors = { ...nameErrors, ...sectionErrors };
    if (Object.keys(allErrors).length === 0) return true;
    notifyWorkoutValidationErrors(errorNotification, allErrors, form.values.sections, actionLabel);
    return false;
  };

  const handleSave = (): void => {
    if (!validateWorkout('save the template')) return;

    const payload = buildPayload(form.values);
    updateMutation.mutate(payload, {
      onSuccess: () => {
        successNotification('Template updated', 'Workout template has been updated.');
        navigate('/workouts/library');
      },
      onError: error => {
        const errorMessage = error.response?.data?.message ?? 'Failed to update template';
        errorNotification('Update failed', errorMessage);
      },
    });
  };

  const handleClose = (): void => {
    navigate('/workouts/library');
  };

  const convertFormToPreviewData = (): WorkoutTemplatePreviewData => {
    const values = form.values;

    return {
      workoutTemplateId: workoutTemplateId,
      name: values.name || 'Untitled Workout',
      athleteNotes: values.athleteNotes || null,
      coachNotes: values.coachNotes || null,
      sections: values.sections
        .filter(section => section.name || section.description)
        .map(section => {
          const synthMovementName = section.scoreEntireSection ? section.name.trim() || null : null;
          return {
            name: section.name || `Section ${section.letter}`,
            workoutInstructions: section.description || '',
            scoreEntireSection: section.scoreEntireSection === true,
            movements: section.movements
              .filter(m => m.scoreType !== null && (synthMovementName || (m.movementId && m.movementName)))
              .map(m => ({
                workoutMovement: {
                  workoutMovementId: m.movementId,
                  name: synthMovementName ?? m.movementName ?? 'Unknown Movement',
                },
                scoreType: m.scoreType as ScoreTypeEnum,
              })),
          };
        }),
    };
  };

  const handlePreview = (): void => {
    openPreviewModal();
  };

  const handleEditFromPreview = (): void => {
    closePreviewModal();
    if (workoutTemplateId) {
      navigate(`/workouts/library/templates/edit/${workoutTemplateId}`);
    }
  };

  return (
    <>
      <ModalWorkoutTemplatePreview
        opened={previewModalOpened}
        template={convertFormToPreviewData()}
        handleClose={closePreviewModal}
        onEdit={handleEditFromPreview}
        showEditButton={true}
      />
      <Box className={classes.page} ref={containerRef}>
        <Box className={classes.content}>
          <header className={classes.header}>
            <div className={classes.headerLeft}>
              <Title order={1} mb='xs'>
                Edit Workout Template
              </Title>
              <Text color='dimmed' size='sm'>
                Design and build your workout program.
              </Text>
            </div>

            <div className={classes.headerActions}>
              <UnstyledButton
                className={classes.previewLink}
                onClick={handlePreview}
                data-cy='previewButton'
                aria-label='Preview'
              >
                <IconDeviceDesktop size={18} />
                <Text component='span'>Preview</Text>
              </UnstyledButton>
              <Button
                onClick={handleSave}
                loading={updateMutation.isLoading}
                disabled={!canMutate || updateMutation.isLoading}
                data-cy='saveButton'
              >
                {updateMutation.isLoading ? 'Saving…' : 'Save Template'}
              </Button>
              <ActionIcon
                onClick={handleClose}
                size='lg'
                variant='subtle'
                color='gray'
                data-cy='closeButton'
                aria-label='Close'
              >
                <IconX size={20} />
              </ActionIcon>
            </div>
          </header>

          <Stack spacing='md'>
            <Card className={classes.card} p='md' radius='md' withBorder shadow='sm'>
              <Stack spacing='md'>
                <TextInput
                  label='Workout Name'
                  placeholder='Enter workout name'
                  required
                  {...form.getInputProps('name')}
                  data-cy='workoutNameInput'
                />
                <ExpandableSection
                  title={
                    <Text size='sm' weight={600}>
                      Athlete Notes
                    </Text>
                  }
                  opened={athleteNotesOpened}
                  handleToggle={toggleAthleteNotes}
                  dataCy='athleteNotesSection'
                >
                  <WorkoutRichTextEditor
                    value={form.getInputProps('athleteNotes').value ?? ''}
                    onChange={html => form.getInputProps('athleteNotes').onChange(html)}
                    dataCy='athleteNotesTextarea'
                  />
                </ExpandableSection>
                <ExpandableSection
                  title={
                    <Group spacing='xs'>
                      <Text size='sm' weight={600}>
                        Coach Notes
                      </Text>
                      <Group spacing={4}>
                        <IconLock size={14} color='gray' />
                        <Text size='sm' color='dimmed'>
                          (Private)
                        </Text>
                      </Group>
                    </Group>
                  }
                  opened={coachNotesOpened}
                  handleToggle={toggleCoachNotes}
                  dataCy='coachNotesSection'
                >
                  <Textarea
                    placeholder='Internal notes for coaches only...'
                    {...form.getInputProps('coachNotes')}
                    minRows={3}
                    data-cy='coachNotesTextarea'
                  />
                </ExpandableSection>
              </Stack>
            </Card>

            {form.values.sections.map((section, index) => (
              <WorkoutSection
                key={index}
                section={section}
                index={index}
                form={form}
                onDelete={handleDeleteSection}
                onDuplicate={handleDuplicateSection}
                canDelete={form.values.sections.length > 1}
              />
            ))}
          </Stack>

          <Box className={classes.addSectionBar}>
            <Button
              className={classes.addSectionButton}
              leftIcon={<IconPlus size={16} />}
              onClick={handleAddSection}
              data-cy='addSectionButton'
            >
              Add Section
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const EditWorkoutTemplate = (): JSX.Element => {
  const { workoutTemplateId } = useParams<{ workoutTemplateId: string }>();
  const { data: templateResponse, isLoading: templateLoading } = useWorkoutTemplate(workoutTemplateId);
  const { classes } = useStyles();

  if (templateLoading || !templateResponse?.data) {
    return (
      <Box className={classes.page}>
        <Box className={classes.content}>
          <Stack align='center' py='xl'>
            <Loader />
            <Text color='dimmed'>Loading workout template...</Text>
          </Stack>
        </Box>
      </Box>
    );
  }

  return <EditWorkoutTemplateForm template={templateResponse.data} workoutTemplateId={workoutTemplateId!} />;
};

export default EditWorkoutTemplate;
